// Per-ASIN action tracking cell for the War Room scorecard table. No status yet →
// "Needs decision" pill + inline status/note form. Status set → colored badge +
// age in days + note on hover + "clear" (which posts a CLEARED action so the
// backend's getLatestScorecard() surfaces it back as no-status on next fetch).
import React, { useState } from 'react';
import ApiClient from '../../utils/ApiClient';

const STATUS_OPTIONS = [
    { value: 'PO_PLACED', label: 'PO placed' },
    { value: 'REPORTED_MAP', label: 'Reported to Schluter' },
    { value: 'WATCHING', label: 'Watching' },
    { value: 'CONCEDED', label: 'Conceded' },
];

const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => {
    acc[o.value] = o.label;
    return acc;
}, {});

const STATUS_STYLES = {
    PO_PLACED: 'bg-blue-100 text-blue-800 border-blue-300',
    REPORTED_MAP: 'bg-purple-100 text-purple-800 border-purple-300',
    WATCHING: 'bg-gray-100 text-gray-800 border-gray-300',
    CONCEDED: 'bg-slate-200 text-slate-700 border-slate-400',
};

function ActionCell({ asin, actionStatus, actionNote, actionAge, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [status, setStatus] = useState(STATUS_OPTIONS[0].value);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const save = async (statusToSave, noteToSave) => {
        setSaving(true);
        setError(null);
        try {
            await ApiClient.post('/api/war-room/action', {
                asin,
                actionStatus: statusToSave,
                note: noteToSave || null,
            });
            setEditing(false);
            setNote('');
            if (onSaved) onSaved();
        } catch (err) {
            console.error('Error saving war room action:', err);
            setError('Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => save('CLEARED', null);

    if (!actionStatus) {
        return (
            <div className="min-w-[190px]">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                    Needs decision
                </span>
                {!editing ? (
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="block mt-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                        Set action
                    </button>
                ) : (
                    <div className="mt-1 space-y-1">
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {STATUS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        <input
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Note (optional)"
                            className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => save(status, note)}
                                className="text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded px-2 py-1"
                            >
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setEditing(false); setError(null); }}
                                className="text-xs text-gray-500 hover:text-gray-700"
                            >
                                Cancel
                            </button>
                        </div>
                        {error && <p className="text-xs text-red-600">{error}</p>}
                    </div>
                )}
            </div>
        );
    }

    const style = STATUS_STYLES[actionStatus] || STATUS_STYLES.WATCHING;
    const label = STATUS_LABELS[actionStatus] || actionStatus;

    return (
        <div className="min-w-[150px]" title={actionNote || ''}>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${style}`}>
                {label}
            </span>
            <div className="mt-1 text-xs text-gray-500">
                {actionAge != null ? `${actionAge}d ago` : ''}
            </div>
            <button
                type="button"
                disabled={saving}
                onClick={handleClear}
                className="mt-0.5 text-xs text-gray-400 hover:text-red-600 disabled:opacity-50"
            >
                {saving ? 'Clearing…' : 'clear'}
            </button>
            {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
    );
}

export default ActionCell;
